import { Button, Chip, Field, Icon, Panel, Seg, Switch } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api, type CatalogEntry, type RoleRow } from "../api.js";
import { askReason, ErrorLine, useAction } from "./common.js";

type Action = CatalogEntry["actions"][number];
const ACTIONS: { key: Action; label: string }[] = [
  { key: "view", label: "دیدن" },
  { key: "create", label: "ثبت" },
  { key: "update", label: "ویرایش" },
  { key: "approve", label: "تأیید" },
];

interface Draft {
  name: string;
  description: string;
  context: "staff" | "customer";
  sensitive: boolean;
  grants: Record<string, Action[]>;
  switches: RoleRow["switches"];
}

const toDraft = (r: RoleRow): Draft => ({
  name: r.name,
  description: r.description,
  context: r.context,
  sensitive: r.sensitive,
  grants: Object.fromEntries(r.permissions.map((g) => [g.resource, g.actions])),
  switches: r.switches,
});
const empty = (): Draft => ({
  name: "",
  description: "",
  context: "staff",
  sensitive: false,
  grants: {},
  switches: { seePrices: false, seeCost: false, addOrderLines: false },
});

/** Role designer (design/v2 Roles.html): permissions × scope × switches; archive, never delete. */
export function RolesPage() {
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<RoleRow[]>("/roles") });
  const catalog = useQuery({ queryKey: ["permissions"], queryFn: () => api<CatalogEntry[]>("/permissions") });
  // A role id, or "new" while creating one.
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(empty);
  const current = roles.data?.find((r) => r.id === selected);

  useEffect(() => {
    if (current) setDraft(toDraft(current));
    if (selected === "new") setDraft(empty());
  }, [selected, current]);

  const visible = useMemo(
    () => (catalog.data ?? []).filter((c) => c.audience === "both" || c.audience === draft.context),
    [catalog.data, draft.context],
  );

  const body = () => ({
    name: draft.name,
    description: draft.description,
    context: draft.context,
    sensitive: draft.context === "staff" && draft.sensitive,
    permissions: Object.entries(draft.grants)
      .filter(([, a]) => a.length)
      .map(([resource, actions]) => ({ resource, actions })),
    scope: draft.context === "customer" ? { kind: "own-units" } : (current?.scope ?? { kind: "all" }),
    switches: draft.switches,
  });

  const save = useAction(async () => {
    if (selected === "new") {
      const { id } = await api<{ id: string }>("/roles", { body: body() });
      setSelected(id);
    } else if (current) {
      await api(`/roles/${current.id}`, { method: "PUT", body: { ...body(), version: current.version } });
    }
  }, [["roles"]]);
  const archive = useAction(
    async (r: RoleRow) => {
      const reason = askReason(r.name);
      if (!reason) return;
      await api(`/roles/${r.id}/archive`, { body: { version: r.version, reason } });
      setSelected(null);
    },
    [["roles"]],
  );

  const toggle = (resource: string, action: Action) => {
    setDraft((d) => {
      const now = d.grants[resource] ?? [];
      const next = now.includes(action) ? now.filter((a) => a !== action) : [...now, action];
      return { ...d, grants: { ...d.grants, [resource]: next } };
    });
  };

  return (
    <div className="grid" style={{ gridTemplateColumns: "320px minmax(0,1fr)", alignItems: "start" }}>
      <Panel
        title="نقش‌ها"
        actions={
          <Button
            variant="primary"
            icon="plus"
            onClick={() => {
              setSelected("new");
            }}
          >
            نقش جدید
          </Button>
        }
      >
        <div className="stack" style={{ gap: "var(--s2)" }}>
          {(roles.data ?? []).map((r) => (
            <button
              key={r.id}
              type="button"
              className={`role-item${r.id === selected ? " is-active" : ""}`}
              onClick={() => {
                setSelected(r.id);
              }}
            >
              <span className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
                <span className="m">{r.name}</span>
                {r.systemKey === "support" && <Chip tone="blue">پشتیبان فنی</Chip>}
              </span>
              <span className="t-cap muted">
                {r.context === "customer" ? "مشتری" : "کارکنان"}،{" "}
                {String(r.activeAssignments).replace(/\d/g, (x) => "۰۱۲۳۴۵۶۷۸۹"[Number(x)] ?? x)} کاربر
              </span>
            </button>
          ))}
        </div>
        <p className="panel-foot t-cap">نقش‌ها حذف نمی‌شوند؛ بایگانی می‌شوند.</p>
      </Panel>

      {selected ? (
        <Panel
          title={selected === "new" ? "نقش جدید" : (current?.name ?? "")}
          actions={
            current?.systemKey === "support" ? (
              <span className="muted t-cap">این دسترسی فقط از سرور تغییر می‌کند</span>
            ) : current && !current.grantsAll ? (
              <Button
                variant="ghost"
                disabled={current.activeAssignments > 0 || archive.busy}
                title={current.activeAssignments > 0 ? "اول کاربرانش را به نقش دیگری ببرید" : undefined}
                onClick={() => {
                  archive.run(current);
                }}
              >
                بایگانی نقش
              </Button>
            ) : undefined
          }
        >
          <div className="stack">
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Field
                id="role-name"
                label="نام نقش"
                value={draft.name}
                onChange={(e) => {
                  setDraft({ ...draft, name: e.target.value });
                }}
              />
              <Field
                id="role-desc"
                label="توضیح"
                value={draft.description}
                onChange={(e) => {
                  setDraft({ ...draft, description: e.target.value });
                }}
              />
            </div>
            {selected === "new" && (
              <Seg
                label="نوع نقش"
                value={draft.context}
                onChange={(v) => {
                  setDraft({ ...draft, context: v, grants: {} });
                }}
                options={[
                  { value: "staff", label: "کارکنان" },
                  { value: "customer", label: "مشتری" },
                ]}
              />
            )}
            {current?.grantsAll ? (
              <div className="alert normal">
                <Icon name="shield" />
                <div>
                  <div className="b">همه‌ی دسترسی‌های کارکنان، از جمله بخش‌های آینده</div>
                  <p>این نقش تأیید دستگاه نقش‌های حساس را هم دارد.</p>
                </div>
              </div>
            ) : (
              <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>بخش</th>
                        {ACTIONS.map((a) => (
                          <th key={a.key} style={{ textAlign: "center" }}>
                            {a.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((c) => (
                        <tr key={c.resource}>
                          <td>
                            <span className="m">{c.label}</span>
                            <br />
                            <span className="t-cap muted">{c.moduleLabel}</span>
                          </td>
                          {ACTIONS.map((a) => {
                            const allowed = c.actions.includes(a.key);
                            const on = draft.grants[c.resource]?.includes(a.key) ?? false;
                            return (
                              <td key={a.key} style={{ textAlign: "center" }}>
                                {allowed ? (
                                  <button
                                    type="button"
                                    className="tick"
                                    aria-pressed={on}
                                    aria-label={`${a.label} ${c.label}`}
                                    onClick={() => {
                                      toggle(c.resource, a.key);
                                    }}
                                  >
                                    <Icon name="check" small />
                                  </button>
                                ) : (
                                  <span className="muted" aria-hidden="true">
                                    —
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="card stack">
              <h3 className="t-lead">سوئیچ‌ها</h3>
              <Switch
                label="دیدن قیمت‌ها"
                checked={draft.switches.seePrices}
                onChange={(v) => {
                  setDraft({ ...draft, switches: { ...draft.switches, seePrices: v } });
                }}
              />
              <Switch
                label="دیدن بهای تمام‌شده"
                checked={draft.switches.seeCost}
                onChange={(v) => {
                  setDraft({ ...draft, switches: { ...draft.switches, seeCost: v } });
                }}
              />
              <Switch
                label="افزودن ردیف به سفارش"
                checked={draft.switches.addOrderLines}
                onChange={(v) => {
                  setDraft({ ...draft, switches: { ...draft.switches, addOrderLines: v } });
                }}
              />
              {draft.context === "staff" && (
                <Switch
                  label="نقش حساس (PIN شش‌رقمی و تأیید دستگاه)"
                  checked={draft.sensitive}
                  onChange={(v) => {
                    setDraft({ ...draft, sensitive: v });
                  }}
                />
              )}
              {draft.context === "customer" && (
                <Chip tone="blue">مشتری فقط برای واحد(های) خودش کار می‌کند</Chip>
              )}
            </div>
            <div className="row" style={{ justifyContent: "flex-end" }}>
              {current?.systemKey === "support" ? (
                <span className="muted t-cap">این دسترسی فقط از سرور تغییر می‌کند</span>
              ) : (
                <>
                  <span className="t-cap muted">هر تغییر در ردپا ثبت می‌شود.</span>
                  <Button
                    variant="primary"
                    size="lg"
                    icon="check"
                    disabled={save.busy || !draft.name}
                    onClick={() => {
                      save.run(undefined);
                    }}
                  >
                    ذخیره‌ی تغییرات
                  </Button>
                </>
              )}
            </div>
            <ErrorLine error={save.error ?? archive.error} />
          </div>
        </Panel>
      ) : (
        <Panel>
          <p className="muted">یک نقش را انتخاب کنید یا نقش جدید بسازید.</p>
        </Panel>
      )}
    </div>
  );
}
